import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Trash2, Star, EyeOff, Check, X, Image as ImageIcon, Sparkles } from "lucide-react";
import ImageField from "@/components/ImageField";

const STATUS_OPTIONS = [
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "hidden", label: "Hidden" },
];

export default function AdminTruthWall() {
  return (
    <div data-testid="admin-truth-wall" className="space-y-10">
      <SeedTools />
      <PageEditor />
      <SubmissionsList />
    </div>
  );
}

function SeedTools() {
  const [busy, setBusy] = useState(false);
  const [aiCount, setAiCount] = useState(10);
  const [artCount, setArtCount] = useState(10);

  const seed = async (source, count) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/truth-wall/seed`, {}, { params: { source, count } });
      toast.success(`Created ${data.created} ${source === "ai" ? "AI-generated" : "artwork-sourced"} truths.`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Seed failed.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-[#C9A055]/30 bg-[#0a0a0a] p-6" data-testid="tw-seed-tools">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-2">Seed Truths</div>
      <div className="font-display text-xl text-[#F4F1EA] mb-1">Populate the wall in seconds</div>
      <p className="font-body text-sm text-[#B5B0A7] mb-5 max-w-xl">
        Two ways to fill the Truth Wall before community submissions kick in. Both create
        <span className="text-[#C9A055]"> auto-approved</span> truths flagged as seeded so you can review later.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-white/10 p-4">
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">AI Generate</div>
          <div className="font-display text-[#F4F1EA] mb-2">Fresh aphorisms</div>
          <p className="font-body text-[12px] text-[#8a857d] mb-3">
            Uses the Universal LLM key. Cost is metered per call.
          </p>
          <div className="flex gap-2 items-center">
            <input type="number" min="1" max="30" value={aiCount}
                   onChange={(e) => setAiCount(Number(e.target.value) || 10)}
                   data-testid="tw-seed-ai-count"
                   className="stc-input w-20 text-sm" />
            <button onClick={() => seed("ai", aiCount)} disabled={busy} data-testid="tw-seed-ai-btn"
                    className="btn-gold text-[11px] flex items-center gap-1.5 disabled:opacity-60">
              <Sparkles size={11} /> Generate
            </button>
          </div>
        </div>
        <div className="border border-white/10 p-4">
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">From Artwork</div>
          <div className="font-display text-[#F4F1EA] mb-2">Pull from your collection</div>
          <p className="font-body text-[12px] text-[#8a857d] mb-3">
            Sources truth lines from your artwork meanings/descriptions. No LLM cost.
          </p>
          <div className="flex gap-2 items-center">
            <input type="number" min="1" max="30" value={artCount}
                   onChange={(e) => setArtCount(Number(e.target.value) || 10)}
                   data-testid="tw-seed-art-count"
                   className="stc-input w-20 text-sm" />
            <button onClick={() => seed("artwork", artCount)} disabled={busy} data-testid="tw-seed-art-btn"
                    className="btn-outline text-[11px] flex items-center gap-1.5 disabled:opacity-60">
              <ImageIcon size={11} /> Pull
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function PageEditor() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/truth-wall/settings").then(({ data }) => setS(data)).catch(() => toast.error("Couldn't load settings."));
  }, []);

  if (!s) return <div className="text-[#8a857d] font-body">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await http.patch("/admin/truth-wall/settings", s);
      toast.success("Saved.");
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };

  return (
    <section className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="tw-page-editor">
      <div className="flex items-center justify-between mb-5">
        <div className="font-display text-xl text-[#F4F1EA]">Truth Wall — Page Content</div>
        <div className="flex items-center gap-3">
          <Toggle label="Page enabled" value={s.enabled !== false} onChange={(v) => set("enabled", v)} testid="tw-toggle-enabled" />
          <button onClick={save} disabled={busy} data-testid="tw-page-save" className="btn-gold flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {busy ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>

      <Field label="Hero image">
        <ImageField value={s.hero_image || ""} onChange={(v) => set("hero_image", v)} testid="tw-hero" />
      </Field>
      <Field label="Title"><input className="stc-input" value={s.title} onChange={(e) => set("title", e.target.value)} maxLength={120} /></Field>
      <Field label="Subtitle"><input className="stc-input" value={s.subtitle} onChange={(e) => set("subtitle", e.target.value)} maxLength={200} /></Field>
      <Field label="Intro paragraph"><textarea className="stc-input min-h-[90px]" value={s.intro} onChange={(e) => set("intro", e.target.value)} maxLength={1200} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Submit CTA"><input className="stc-input" value={s.submit_cta} onChange={(e) => set("submit_cta", e.target.value)} maxLength={60} /></Field>
        <Field label="View CTA"><input className="stc-input" value={s.view_cta} onChange={(e) => set("view_cta", e.target.value)} maxLength={60} /></Field>
      </div>
      <Field label="Guidelines text"><textarea className="stc-input min-h-[120px]" value={s.guidelines} onChange={(e) => set("guidelines", e.target.value)} maxLength={2000} /></Field>

      <div className="grid sm:grid-cols-3 gap-3 mt-2">
        <Toggle label="Voting enabled" value={s.voting_enabled} onChange={(v) => set("voting_enabled", v)} testid="tw-toggle-voting" />
        <Toggle label="Show vote counts" value={s.show_votes} onChange={(v) => set("show_votes", v)} testid="tw-toggle-show-votes" />
        <Toggle label="Allow anonymous" value={s.allow_anonymous} onChange={(v) => set("allow_anonymous", v)} testid="tw-toggle-anon" />
      </div>
    </section>
  );
}

function SubmissionsList() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    http.get("/admin/truth-wall/submissions", { params: filter === "all" ? {} : { status: filter } })
      .then(({ data }) => setRows(data || []))
      .catch(() => toast.error("Couldn't load submissions."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const patch = async (id, body) => {
    try {
      await http.patch(`/admin/truth-wall/truths/${id}`, body);
      toast.success("Updated.");
      reload();
    } catch { toast.error("Update failed."); }
  };
  const del = async (id) => {
    if (!confirm("Delete this truth permanently?")) return;
    try { await http.delete(`/admin/truth-wall/truths/${id}`); toast.success("Deleted."); reload(); }
    catch { toast.error("Delete failed."); }
  };
  const resetVotes = async (id) => {
    if (!confirm("Reset vote count to 0?")) return;
    try { await http.post(`/admin/truth-wall/truths/${id}/reset-votes`); toast.success("Votes reset."); reload(); }
    catch { toast.error("Reset failed."); }
  };

  return (
    <section className="border border-white/10 bg-[#0a0a0a] p-6" data-testid="tw-submissions">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="font-display text-xl text-[#F4F1EA]">Submissions ({rows.length})</div>
        <div className="flex gap-1.5">
          {["pending", "approved", "rejected", "hidden", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`tw-filter-${f}`}
                    className={`font-ui text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 border ${filter === f ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/15 text-[#8a857d] hover:text-[#F4F1EA]"}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-[#8a857d] font-body">Loading…</div> : rows.length === 0 ? (
        <div className="text-center border border-dashed border-white/10 p-10 text-[#8a857d] font-body">No truths in this view.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((t) => (
            <SubmissionRow key={t.id} t={t} onPatch={patch} onDelete={del} onResetVotes={resetVotes} />
          ))}
        </div>
      )}
    </section>
  );
}

function SubmissionRow({ t, onPatch, onDelete, onResetVotes }) {
  const [draft, setDraft] = useState({ text: t.text, story: t.story || "", admin_note: t.admin_note || "" });
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-white/10 bg-[#050505] p-4" data-testid={`tw-row-${t.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <textarea
            className="stc-input min-h-[50px] text-[#F4F1EA] font-display text-[16px]"
            value={draft.text}
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            data-testid={`tw-row-text-${t.id}`}
          />
          <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-2 flex flex-wrap gap-x-3 gap-y-1">
            <span>{t.name || (t.anonymous ? "Anonymous" : "—")}</span>
            {t.email && <span>{t.email}</span>}
            {t.city && <span>{t.city}{t.state ? `, ${t.state}` : ""}</span>}
            <span>· Votes: {t.votes ?? 0}</span>
            <span>· Status: <span className={t.status === "approved" ? "text-emerald-400" : "text-[#C9A055]"}>{t.status}</span></span>
            {t.featured && <span className="text-[#C9A055]">· Featured ★</span>}
          </div>
          <button onClick={() => setExpanded(!expanded)} className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mt-2">
            {expanded ? "Collapse" : "Edit details"}
          </button>
          {expanded && (
            <div className="mt-3 space-y-3">
              <textarea className="stc-input min-h-[70px]" placeholder="Story" value={draft.story}
                        onChange={(e) => setDraft({ ...draft, story: e.target.value })} />
              <textarea className="stc-input min-h-[50px]" placeholder="Admin note (private)" value={draft.admin_note}
                        onChange={(e) => setDraft({ ...draft, admin_note: e.target.value })} />
              <button onClick={() => onPatch(t.id, draft)} className="btn-outline flex items-center gap-1.5 text-[11px]" data-testid={`tw-save-${t.id}`}>
                <Save size={11} /> Save edits
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <StatusSelect value={t.status} onChange={(v) => onPatch(t.id, { status: v })} testid={`tw-status-${t.id}`} />
          <button onClick={() => onPatch(t.id, { featured: !t.featured })} title="Feature"
                  data-testid={`tw-feature-${t.id}`}
                  className={`btn-outline text-[10px] flex items-center justify-center gap-1 ${t.featured ? "border-[#C9A055] text-[#C9A055]" : ""}`}>
            <Star size={11} /> {t.featured ? "Unfeature" : "Feature"}
          </button>
          <button onClick={() => onResetVotes(t.id)} className="btn-outline text-[10px] flex items-center justify-center gap-1">
            <RotateCcw size={11} /> Reset votes
          </button>
          <button onClick={() => onDelete(t.id)} className="btn-outline text-[10px] border-red-700 text-red-400 flex items-center justify-center gap-1">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusSelect({ value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="stc-input text-[11px] py-1 w-[120px]">
      {STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 font-body text-sm text-[#F4F1EA] cursor-pointer" data-testid={testid}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-[#C9A055]" />
      {label}
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-4">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
